import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import formatSalary from '@/utils/format-salary'
import { isNullOrEmpty } from '@/utils'
import FormatProfession from '@/components/format-profession'
import { DisplayTable } from '@/features/Application/elements/tables'
import {
  LabeledValue,
  LabelText,
  ValueText,
  PhoneLink,
  EmailLink,
  Wrapper,
  EmptyComponent,
  BasicSubTitle
} from '@/features/Application/elements'

const RoommateInformation = ({ application = {}, applicationLoading }) => {
  const { t } = useTranslation()
  const roommates = application?.user_roommates ?? []
  const title = t('l.application.view.roommate_information.label', 'Roommate Information')
  const roommateStyle = '1fr 1fr'

  const data = useMemo(
    () =>
      roommates.map(roommate => ({
        subTitle: <BasicSubTitle user={roommate} />,
        items: [
          {
            id: 'Phone Number',
            cell: (
              <LabeledValue
                key="Phone Number"
                label={
                  <LabelText>
                    {t('l.application.view.phone_number.label', 'Phone Number')}
                  </LabelText>
                }
                value={
                  <PhoneLink
                    phoneNumber={roommate.phone_number}
                    countryCode={roommate.phone_country_code_alpha}
                  />
                }
                templateColumns={roommateStyle}
              />
            )
          },
          {
            id: 'Email',
            cell: (
              <LabeledValue
                key="Email"
                label={<LabelText>{t('l.application.view.email.label', 'Email')}</LabelText>}
                value={<EmailLink email={roommate.email} />}
                templateColumns={roommateStyle}
              />
            )
          },
          {
            id: 'Job Title',
            cell: (
              <LabeledValue
                key="Job Title"
                label={
                  <LabelText>{t('l.application.view.job_title.label', 'Job Title')}</LabelText>
                }
                value={
                  <ValueText>
                    {!isNullOrEmpty(roommate.job_title) ? (
                      <FormatProfession profession={roommate.job_title} />
                    ) : (
                      '-'
                    )}
                  </ValueText>
                }
                templateColumns={roommateStyle}
              />
            )
          },
          {
            id: 'Annual Income',
            cell: (
              <LabeledValue
                key="Annual Income"
                label={
                  <LabelText>
                    {t('l.application.view.annual_income.label', 'Annual Income')}
                  </LabelText>
                }
                value={
                  <ValueText>
                    {!isNullOrEmpty(roommate.salary) ? formatSalary(roommate.salary, t) : '-'}
                  </ValueText>
                }
                templateColumns={roommateStyle}
              />
            )
          },
          {
            id: 'Date of Birth',
            cell: (
              <LabeledValue
                key="Date of Birth"
                label={
                  <LabelText>
                    {t('l.application.view.date_of_birth_dob.label', 'Date of Birth')}
                  </LabelText>
                }
                value={<ValueText>{!isNullOrEmpty(roommate.dob) ? roommate.dob : '-'}</ValueText>}
                templateColumns={roommateStyle}
              />
            )
          },
          {
            id: 'Relationship',
            cell: (
              <LabeledValue
                key="Relationship"
                label={
                  <LabelText>
                    {t('l.application.view.relationship.label', 'Relationship')}
                  </LabelText>
                }
                value={
                  <ValueText>
                    {t(`basic:b.contact.relationships.${roommate.relationship}.text`)}
                  </ValueText>
                }
                templateColumns={roommateStyle}
              />
            )
          }
        ]
      })),
    [applicationLoading]
  )

  return (
    <Wrapper>
      <DisplayTable
        data={data}
        title={title}
        columns={1}
        subColumns={2}
        emptyComponent={
          <EmptyComponent isLoading={applicationLoading}>
            {t(
              'l.application.view.no_roommates.label',
              'No roommates included in this application.'
            )}
          </EmptyComponent>
        }
      />
    </Wrapper>
  )
}

export default RoommateInformation
